import {
  NETWORK_PALLET_TYPES,
  PalletFamily,
  PalletTier,
  SERVICE_LEVELS,
  ServiceLevel,
  PALLET_UNIT,
} from './types'

type Alias<T> = [phrase: string, value: T]

const byLongestPhraseFirst = <T>(aliases: Alias<T>[]): Alias<T>[] =>
  [...aliases].sort((a, b) => b[0].length - a[0].length)

const SERVICE_LEVEL_ALIASES = byLongestPhraseFirst<ServiceLevel>([
  ['same day', 'Same Day'],
  ['sameday', 'Same Day'],
  ['next day', 'Next Day'],
  ['nextday', 'Next Day'],
  ['nd', 'Next Day'],
  ['24 hr', 'Next Day'],
  ['24 hrs', 'Next Day'],
  ['24 h', 'Next Day'],
  ['24 hour', 'Next Day'],
  ['24 hours', 'Next Day'],
  ['24', 'Next Day'],
  ['premium', 'Next Day'],
  ['overnight', 'Next Day'],
  ['economy', 'Economy'],
  ['econ', 'Economy'],
  ['eco', 'Economy'],
  ['eec', 'Economy'],
  ['48 hr', 'Economy'],
  ['48 hrs', 'Economy'],
  ['48 h', 'Economy'],
  ['48 hour', 'Economy'],
  ['48 hours', 'Economy'],
  ['48', 'Economy'],
  ['2 day', 'Economy'],
  ['2 days', 'Economy'],
  ['two day', 'Economy'],
  ['super economy', '3 Day'],
  ['up to 3 days', '3 Day'],
  ['3 day', '3 Day'],
  ['3 days', '3 Day'],
  ['three day', '3 Day'],
  ['72 hr', '3 Day'],
  ['72 hrs', '3 Day'],
  ['72 h', '3 Day'],
  ['72 hour', '3 Day'],
  ['72 hours', '3 Day'],
  ['72', '3 Day'],
  ['up to 5 days', '5 Day'],
  ['5 day', '5 Day'],
  ['5 days', '5 Day'],
  ['five day', '5 Day'],
  ['3 5 days', '5 Day'],
  ['3 to 5 days', '5 Day'],
])

const PALLET_TIER_ALIASES = byLongestPhraseFirst<PalletTier>([
  ['quarter pallet', 'Quarter'],
  ['quarter', 'Quarter'],
  ['qtr', 'Quarter'],
  ['q pt', 'Quarter'],
  ['qpt', 'Quarter'],
  ['1 4', 'Quarter'],
  ['half pallet', 'Half'],
  ['half', 'Half'],
  ['hlf', 'Half'],
  ['1 2', 'Half'],
  ['full pallet', 'Full'],
  ['full', 'Full'],
])

const PALLET_FAMILY_ALIASES = byLongestPhraseFirst<PalletFamily>([
  ['european', 'EU'],
  ['euro', 'EU'],
  ['eu', 'EU'],
  ['gb', 'GB'],
  ['uk', 'GB'],
])

const OVERSIZE_WORDS = ['oversize', 'oversized', 'os']
const OVERSIZE_HALF_WORDS = ['oshlf', 'oshalf']
const OVERSIZE_FULL_WORDS = ['osfull', 'osfl']
const LIGHT_FULL_WORDS = ['light', 'lite', 'flite']

const UNSUPPORTED_PALLET_WORDS = ['parcel', 'mini', 'micro', 'double']

export const toSpacedWords = (label: string): string =>
  ' ' +
  label
    .toLowerCase()
    .replace(/(\d)([a-z])/g, '$1 $2')
    .replace(/([a-z])(\d)/g, '$1 $2')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim() +
  ' '

export const containsPhrase = (words: string, phrase: string): boolean => words.includes(` ${phrase} `)

const firstAliasMatch = <T>(words: string, aliases: Alias<T>[]): T | null => {
  const match = aliases.find(([phrase]) => containsPhrase(words, phrase))
  return match ? match[1] : null
}

export function normaliseServiceLevel(label: string | undefined | null): ServiceLevel | null {
  if (!label) return null
  const exact = SERVICE_LEVELS.find((level) => level.toLowerCase() === label.trim().toLowerCase())
  if (exact) return exact
  return firstAliasMatch(toSpacedWords(label), SERVICE_LEVEL_ALIASES)
}

export function normalisePalletType(
  label: string | undefined | null,
  fallbackFamily: PalletFamily = 'GB'
): string | null {
  if (!label) return null
  const exact = NETWORK_PALLET_TYPES.find((type) => type.value.toLowerCase() === label.trim().toLowerCase())
  if (exact) return exact.value
  const words = toSpacedWords(label)
  if (UNSUPPORTED_PALLET_WORDS.some((word) => containsPhrase(words, word))) return null
  if (OVERSIZE_HALF_WORDS.some((word) => containsPhrase(words, word))) return 'Oversize Half Pallet'
  if (OVERSIZE_FULL_WORDS.some((word) => containsPhrase(words, word))) return 'Oversize Pallet'
  if (OVERSIZE_WORDS.some((word) => containsPhrase(words, word))) {
    const oversizeTier = firstAliasMatch(words, PALLET_TIER_ALIASES)
    if (oversizeTier === 'Half') return 'Oversize Half Pallet'
    if (oversizeTier === 'Full') return 'Oversize Pallet'
    return null
  }
  if (LIGHT_FULL_WORDS.some((word) => containsPhrase(words, word))) return 'Custom Pallet'
  const tier = firstAliasMatch(words, PALLET_TIER_ALIASES)
  if (!tier) return null
  const family = firstAliasMatch(words, PALLET_FAMILY_ALIASES) ?? fallbackFamily
  return NETWORK_PALLET_TYPES.find((type) => type.family === family && type.tier === tier)?.value ?? null
}

export interface ImportedServiceLabels {
  serviceName: string
  palletType?: string
}

export interface NormalisedImportedService {
  serviceName: string
  palletType?: string
}

export function normaliseImportedService(
  imported: ImportedServiceLabels,
  unit: string = PALLET_UNIT
): NormalisedImportedService {
  const level = normaliseServiceLevel(imported.serviceName) ?? normaliseServiceLevel(imported.palletType)
  const isPalletService = unit === PALLET_UNIT
  const palletType = isPalletService
    ? normalisePalletType(imported.palletType) ?? normalisePalletType(imported.serviceName)
    : null
  const verbatimPalletType = imported.palletType?.trim()
  return {
    serviceName: level ?? imported.serviceName.trim(),
    ...(palletType || verbatimPalletType ? { palletType: palletType ?? verbatimPalletType } : {}),
  }
}
